import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchLatestCurrencyRate } from '../../services/currency';

const CURRENCY_CACHE_KEY = 'currency_latest_rate';

const loadCachedRate = () => {
  try {
    const raw = localStorage.getItem(CURRENCY_CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data && typeof data.nairaPerYuan === 'number' && data.nairaPerYuan > 0) return data.nairaPerYuan;
    return null;
  } catch {
    return null;
  }
};

const saveCachedRate = (nairaPerYuan) => {
  try {
    localStorage.setItem(CURRENCY_CACHE_KEY, JSON.stringify({ nairaPerYuan, cachedAt: new Date().toISOString() }));
  } catch (e) {
    // ignore
  }
};

export const loadLatestCurrencyRate = createAsyncThunk(
  'currency/loadLatest',
  async (_, { rejectWithValue }) => {
    try {
      const rate = await fetchLatestCurrencyRate();
      return rate;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to load currency rate');
    }
  }
);

const initialState = {
  rate: null,
  /** Naira per 1 Yuan - multiply product price (Yuan) by this to get display price in Naira */
  nairaPerYuan: loadCachedRate(),
  loading: false,
  error: null,
};

const currencySlice = createSlice({
  name: 'currency',
  initialState,
  reducers: {
    clearCurrencyRate: (state) => {
      state.rate = null;
      state.nairaPerYuan = null;
      state.error = null;
      try {
        localStorage.removeItem(CURRENCY_CACHE_KEY);
      } catch (e) {}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadLatestCurrencyRate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadLatestCurrencyRate.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.rate = action.payload;
        const r = action.payload;
        if (r && (r.base_currency_rate != null || r.target_currency_rate != null)) {
          const baseCode = (r.base_currency_code || '').toUpperCase();
          const targetCode = (r.target_currency_code || '').toUpperCase();
          if (baseCode === 'NGN' && targetCode === 'CNY') {
            state.nairaPerYuan = Number(r.base_currency_rate) || 0;
          } else if (baseCode === 'CNY' && targetCode === 'NGN') {
            state.nairaPerYuan = Number(r.target_currency_rate) || 0;
          } else {
            state.nairaPerYuan = Number(r.base_currency_rate) || Number(r.target_currency_rate) || 0;
          }
          if (state.nairaPerYuan > 0) saveCachedRate(state.nairaPerYuan);
        }
      })
      .addCase(loadLatestCurrencyRate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load rate';
        if (!state.nairaPerYuan) state.nairaPerYuan = null;
      });
  },
});

export const { clearCurrencyRate } = currencySlice.actions;

export const selectCurrencyRate = (state) => state.currency.rate;
export const selectNairaPerYuan = (state) => state.currency.nairaPerYuan;
export const selectCurrencyLoading = (state) => state.currency.loading;
export const selectCurrencyError = (state) => state.currency.error;

/**
 * Convert price in Yuan (RMB) to display value in Naira.
 * If no rate is loaded, returns the original number (fallback).
 */
export const toDisplayNaira = (priceInYuan, nairaPerYuan) => {
  const yuan = Number(priceInYuan);
  if (Number.isNaN(yuan)) return 0;
  const rate = Number(nairaPerYuan);
  if (!rate || rate <= 0) return yuan;
  return yuan * rate;
};

/**
 * Format a number as Naira (e.g. ₦220,000).
 */
export const formatNaira = (num) => {
  const n = Number(num);
  if (Number.isNaN(n)) return '₦0';
  return `₦${Math.round(n).toLocaleString()}`;
};

export default currencySlice.reducer;
